import { useEffect, useId, useState, type ReactNode } from "react";
import { MIS_3005_D_TABLE_1 } from "@sts/rules-data";
import type { SurveyRoom } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useSubmit } from "../app/use-submit.js";
import { roomTypeWords } from "../lib/survey-validation.js";
import { Failure } from "./Async.jsx";

/**
 * What a room is called, and what it is for.
 *
 * A scan measures shape and cannot tell a bedroom from a bathroom, so it names
 * what it finds "Room 5" and guesses a type. Both matter to the answer: the
 * TYPE sets the room's design temperature from MIS 3005-D Table 1, and the
 * temperature is half of every watt the room is charged. A survey full of
 * "Room 5" at the wrong temperature is a heat loss nobody can check against
 * the house.
 *
 * ON THE ROOM'S OWN SCREEN, AT THE TOP. It was only editable in the panel
 * beside the plan — the room screen could show you "Room 5" and give you no
 * way to say it is the airing cupboard, which is the moment you know.
 *
 * THE TEMPERATURE IS SHOWN BESIDE EACH TYPE, because that is what choosing one
 * does. A dropdown of words hides the consequence; "Bathroom (22 °C)" does not.
 */

const ROOM_TYPES = Object.keys(MIS_3005_D_TABLE_1.data) as (keyof typeof MIS_3005_D_TABLE_1.data)[];

export function RoomIdentity({
  room,
  canEdit,
  onSaved,
}: {
  readonly room: SurveyRoom;
  readonly canEdit: boolean;
  readonly onSaved: () => void;
}): ReactNode {
  const { client } = usePortal();
  const save = useSubmit();
  const nameId = useId();
  const typeId = useId();

  const [name, setName] = useState(room.name);
  const [type, setType] = useState<SurveyRoom["type"]>(room.type);

  // A different room selected, or a re-read after saving, is different values.
  useEffect(() => {
    setName(room.name);
    setType(room.type);
  }, [room.id, room.name, room.type]);

  const dirty = name.trim() !== room.name || type !== room.type;

  /*
   * A TYPED TEMPERATURE CAN REFUSE A CHANGE OF TYPE.
   *
   * The API checks the temperature against the type the room ENDS UP with, so
   * a room somebody set to 21 °C by hand cannot become a bathroom, whose Table
   * 1 minimum is 22 — the whole request is refused, the name with it. A
   * surveyor renaming "Room 5" to the shower room would have got a refusal
   * about a temperature they never typed and cannot see on this screen.
   *
   * So it is said BEFORE the button is pressed, and saving then puts the room
   * back on its type's own minimum. Null, not the new number: the point of
   * "what is it for" is that the type decides the temperature, and writing a
   * figure of our own would invent one nobody chose.
   */
  const minimumForType = MIS_3005_D_TABLE_1.data[type];
  const typedTemp = room.designTempC;
  const willReset = typedTemp !== null && typedTemp < minimumForType;

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!dirty) return;
    void save.submit(async () => {
      await client.updateRoomDetails(room.id, {
        name: name.trim(),
        type,
        ...(willReset ? { designTempC: null } : {}),
      });
      onSaved();
    });
  };

  return (
    <form
      className="room-identity"
      aria-label={`What ${room.name} is`}
      onSubmit={submit}
      data-testid="room-identity"
    >
      <div className="room-identity__field">
        <label htmlFor={nameId}>What is this room called?</label>
        <input
          id={nameId}
          value={name}
          disabled={!canEdit || save.busy}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className="room-identity__field">
        <label htmlFor={typeId}>What is it for?</label>
        <select
          id={typeId}
          value={type}
          disabled={!canEdit || save.busy}
          onChange={(event) => setType(event.target.value as SurveyRoom["type"])}
        >
          {ROOM_TYPES.map((option) => (
            <option key={option} value={option}>
              {roomTypeWords(option)} ({MIS_3005_D_TABLE_1.data[option]} °C)
            </option>
          ))}
        </select>
      </div>

      {canEdit ? (
        <button type="submit" className="primary" disabled={!dirty || save.busy}>
          {save.busy ? "Saving…" : "Save"}
        </button>
      ) : null}

      {willReset ? (
        <p className="room-identity__note" data-testid="room-temp-will-reset">
          This room was set to {typedTemp} °C by hand, and a{" "}
          {roomTypeWords(type).toLowerCase()} is never below {minimumForType} °C. Saving will put
          it back on {minimumForType} °C.
        </p>
      ) : null}

      {/* The scan's own guess, said plainly: a type nobody has confirmed is the
          difference between a bathroom at 22 °C and a landing at 18 °C, and
          nothing else on the screen would tell you it was guessed. */}
      {room.typeAssumed === true ? (
        <p className="room-identity__guessed" data-testid="room-type-guessed">
          The scan could not tell what this room is, so it has been treated as a{" "}
          {roomTypeWords(room.type)} at {MIS_3005_D_TABLE_1.data[room.type]} °C. Change it if that
          is wrong — the temperature is half of every watt this room is charged.
        </p>
      ) : null}

      <Failure error={save.error} what="saving this room" />
    </form>
  );
}
